import * as Yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum TagwallDeliveryTicketLineItemKeys {
  itemNumber = 'itemNumber',
  quantity = 'quantity',
  product = 'product',
  description = 'description',
  quantityDelivered = 'quantityDelivered',
  tag = 'tag',
}

const TagwallLineItemSchema = Yup.object().shape({
  [TagwallDeliveryTicketLineItemKeys.itemNumber]: Yup.number().nullable().optional(),
  [TagwallDeliveryTicketLineItemKeys.quantity]: Yup.string().nullable().optional(),
  [TagwallDeliveryTicketLineItemKeys.description]: Yup.string().nullable().optional(),
  [TagwallDeliveryTicketLineItemKeys.product]: Yup.string().nullable().optional(),
  [TagwallDeliveryTicketLineItemKeys.quantityDelivered]: Yup.string().nullable().optional(),
  [TagwallDeliveryTicketLineItemKeys.tag]: Yup.string().nullable().optional(),
})

export const tagwallDeliveryTicketFileDataSchema = Yup.object().shape({
  organizationLogoOverride: Yup.string().nullable().optional(),
  specifiedFloor: Yup.string().nullable().optional(),
  carrier: Yup.string().nullable().optional(),
  shipDate: Yup.string().nullable().optional(),
  deliveryDate: Yup.string().nullable().optional(),
  instructions: Yup.string().nullable().optional(),
  metalLength: Yup.string().nullable().optional(),
  contact: Yup.object()
    .shape({
      name: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
    })
    .required(),
  projectManager: Yup.object()
    .shape({
      name: Yup.string().nullable().optional(),
      email: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
    })
    .notRequired(),
  coordinator: Yup.object()
    .shape({
      name: Yup.string().nullable().optional(),
      email: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
    })
    .notRequired(),
  organization: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      website: Yup.string().nullable().optional(),
    })
    .required(),
  billTo: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
    })
    .required(),
  shipFrom: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
    })
    .notRequired(),
  purchaseOrderNumber: Yup.string().nullable().optional(),
  proposalNumber: Yup.string().nullable().optional(),
  proposalText: Yup.string().nullable().optional(),
  externalPurchaseOrderNumber: Yup.string().nullable().optional(),
  date: Yup.string().nullable().optional(),
  installAt: Yup.object()
    .shape({
      company: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
    })
    .required(),
  items: Yup.array().of(TagwallLineItemSchema).required(),
  items2: Yup.array().of(TagwallLineItemSchema).notRequired(),
  items3: Yup.array().of(TagwallLineItemSchema).notRequired(),
  items4: Yup.array().of(TagwallLineItemSchema).notRequired(),
  mustShipComplete: Yup.string().nullable().optional(),
})

export type TagwallDeliveryTicketFileData = Yup.InferType<
  typeof tagwallDeliveryTicketFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<TagwallDeliveryTicketFileData>

export const tagwallDeliveryTicketFileMappings: RequiredFileData = {
  organizationLogoOverride: 'a97115c4-3992-4967-9af3-4925db0aa89a',
  deliveryDate: '8feac70a-d751-4878-bbed-e98f91418f86',
  specifiedFloor: '11215db4-e0e8-43c0-8785-f46322bea04a',
  carrier: '62a652b4-d9b9-4891-bd41-490e7bb1633a',
  shipDate: '7d8cf3ea-c533-4ad3-a3cc-13390acf444a',
  instructions: '26cc2408-2c34-44f9-9d58-236fd6a18d5b',
  metalLength: '217341f7-a03f-4cea-95b5-1f86e3931a45',
  contact: {
    name: '8fd75279-324b-4098-b219-9b35d70ac95e',
    phoneNumber: '1d7dc63d-bd07-484f-a286-7b13649d5e48',
  },
  projectManager: {
    name: 'b8f7e6d5-c4a3-4b2c-9d1e-8f7a6b5c4d3e',
    email: 'a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5f',
    phoneNumber: 'f9e8d7c6-b5a4-4c3d-2e1f-0a9b8c7d6e5f',
  },
  coordinator: {
    name: '1a2b3c4d-5e6f-4b7c-8d9e-0f1a2b3c4d5e',
    email: 'e9d8c7b6-a5f4-4d3c-2e1f-0b9a8c7d6e5f',
    phoneNumber: '2b3c4d5e-6f7a-4c8d-9e0f-1a2b3c4d5e6f',
  },
  organization: {
    companyName: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
    addressLine1: '9c858901-8006-4a4d-a4bf-df7b6f5d3a6f',
    addressLine2: 'a5d6b7c8-1234-4f3a-bcde-567890abcdef',
    cityStateZip: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
    phoneNumber: '4c4aef30-58b7-47bf-b6a1-17d6b3e7d4f1',
    website: '58d3a6f7-1b2c-4d5e-9a0b-cdef12345678',
  },
  billTo: {
    companyName: '49dc0b26-e6b0-4192-9bbd-74ca15405906',
    addressLine1: 'a187e1bf-a302-4e29-8e82-ff13d9f3abf8',
    addressLine2: '1063442d-ea6f-4f69-ba4c-7370ba22a726',
    cityStateZip: '6796f429-2b0b-415d-94b3-f682a362eb5a',
  },
  shipFrom: {
    companyName: 'a2b7a36c-9d46-4ad8-8f43-9c69f2743081',
    addressLine1: '64f071b6-bc7e-4be3-83d0-d656d3f3dc78',
    addressLine2: '996936f4-55a6-41f9-92b0-696e197d02a6',
    cityStateZip: 'eecb65bb-32d5-4ba7-8f2b-1b026e057781',
  },
  purchaseOrderNumber: '023bbb46-97c9-4cb2-9f5a-63d9d744675d',
  proposalNumber: '32c1abd5-1960-45fa-a288-e725467c09ed',
  proposalText: '2ca9a1f0-7a69-4bf4-9e54-19c09dd78d8a',
  externalPurchaseOrderNumber: '7cd09729-e512-45db-863a-c99a8087c7b5',
  date: '1d222770-ceaa-4fac-b950-069b2710b693',
  installAt: {
    company: '5a9af3cb-9c34-4f9d-8b1f-687d8e329c97',
    addressLine1: '72acd4ff-c677-4538-a96f-741017db4046',
    addressLine2: '70f64938-7c5f-4d1c-bea9-05801ba1459b',
    cityStateZip: '23e8db6a-341a-4c6f-b528-e1ee0c02cea7',
  },
  mustShipComplete: '6a7d951d-d476-4cee-9e09-172b52d3fa7f',
  items: [
    {
      itemNumber: null,
      quantity: 'ea20fbb3-1527-4b0d-812a-98e3fc03ba1d',
      description: 'f2ba8291-ad71-4bee-9243-996149cda93d',
      product: 'a874afed-1af2-47ad-9288-7d6df35fe40e',
      quantityDelivered: 'e3a5fb2f-e74d-4abf-a075-eb99f7ec30c5',
      tag: '40d5c471-8a17-4845-bcea-cc4ff22a3399',
    },
  ],
  items2: [
    {
      itemNumber: null,
      quantity: 'd1f90e7a-b803-43aa-9c68-7d72b2708695',
      description: '228a7747-6ab5-452e-bc4a-e8204f2b4b7b',
      product: 'e61285b6-11f6-4b47-b054-05e0078f0bc2',
      quantityDelivered: '8d9e0f1a-2b3c-4d5e-6f78-901234567890',
      tag: 'e10f69e2-f118-4985-8fb4-50bf85cd8fea',
    },
  ],
  items3: [
    {
      itemNumber: null,
      quantity: 'c9e8ad1b-42cb-42d9-aeb0-053e3b80da46',
      description: '36707e36-79d4-4c8a-9f1f-0d990b43f39d',
      product: '232ade31-86ae-4dcd-9fa0-64b79accd4a5',
      quantityDelivered: '11e0a3fc-6560-423e-91e2-f76bbe5e4bc7',
      tag: '094a7db3-71fe-445e-87a5-9bd2a28f5044',
    },
  ],
  items4: [
    {
      itemNumber: null,
      quantity: '0a2f1118-5fcc-45b1-b2d4-d0a7827aef47',
      description: '7ac027a2-4184-4cfe-bdbb-69c27bfcd4bb',
      product: 'e2e0be60-75c0-43e6-8805-e44017c8f6c4',
      quantityDelivered: '5ee95069-c9b1-4888-9a70-14746fd773be',
      tag: '753df795-5214-4d4b-8946-a06b8edc02bd',
    },
  ],
}
